import { Button, Text, Tooltip, Flex, Box, LoadingOverlay, TextInput } from '@mantine/core';
import { DatePickerInput } from '@mantine/dates';
import { useEffect, useState } from 'react';
import { useTranslationMipManage } from '@dealstack/frontend/shared/mechanism/i18n/i18nHooks.tsx';
import { drawerTransitionProps } from '@dealstack/frontend/shared/mechanism/styleguide/GlobalStyling';
import {
	FormField,
	StyledDrawer,
} from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/ProgrammeSettings/MasterDocuments/styles.ts';
import { Icon } from '@dealstack/frontend/shared/components/fundament/Icon/Icon';
import { AdvancedSwitch } from '@dealstack/frontend/shared/components/Switch/AdvancedSwitch.tsx';

interface SecurityWithValue {
	id: string;
	name: string;
	fairMarketValuePerUnit: number | null;
	currency: string;
}

interface Props {
	opened: boolean;
	onClose: () => void;
	valuationDate: Date | null;
	securityValuationDate: Date | null;
	shouldShareWithInvestors: boolean | undefined;
	aSecurityHasAValuePerUnit: boolean;
	securities: SecurityWithValue[];
	onSave: (valuationDate: Date | null, shareWithInvestors: boolean) => void;
	onUpdateSecurities: (securities: SecurityWithValue[]) => void;
	isLoading: boolean;
}

export const ValuationSettingsDrawer = ({
	opened,
	onClose,
	valuationDate: initialValuationDate,
	securityValuationDate,
	shouldShareWithInvestors,
	aSecurityHasAValuePerUnit,
	securities,
	onSave,
	onUpdateSecurities,
	isLoading,
}: Props) => {
	const tMipManage = useTranslationMipManage();
	const [valuationDate, setValuationDate] = useState<Date | null>(initialValuationDate);
	const [shareWithInvestors, setShareWithInvestors] = useState<boolean>(
		shouldShareWithInvestors ?? false
	);
	const [securityValues, setSecurityValues] = useState<Record<string, string>>({});

	useEffect(() => {
		if (opened) {
			const dateToUse =
				initialValuationDate ||
				(securityValuationDate ? new Date(securityValuationDate) : null);
			setValuationDate(dateToUse);
			setShareWithInvestors(shouldShareWithInvestors ?? false);

			const initialValues: Record<string, string> = {};
			securities.forEach((security) => {
				if (security.fairMarketValuePerUnit !== null) {
					initialValues[security.id] = security.fairMarketValuePerUnit.toString();
				}
			});
			setSecurityValues(initialValues);
		}
	}, [opened, initialValuationDate, shouldShareWithInvestors, securities, securityValuationDate]);

	const showShareWithInvestorsSwitch =
		(valuationDate || securityValuationDate) && aSecurityHasAValuePerUnit;

	const handleSecurityValueChange = (securityId: string, value: string) => {
		setSecurityValues((prev) => ({
			...prev,
			[securityId]: value,
		}));
	};

	const handleUpdate = async () => {
		const updatedSecurities: SecurityWithValue[] = securities.map((security) => ({
			...security,
			fairMarketValuePerUnit: securityValues[security.id]
				? parseFloat(securityValues[security.id])
				: null,
		}));

		await onUpdateSecurities(updatedSecurities);

		onSave(valuationDate, shareWithInvestors);
	};

	const hasChanges = () => {
		if (
			securityValuationDate &&
			valuationDate?.toISOString() !== new Date(securityValuationDate).toISOString()
		) {
			return true;
		}

		if (!securityValuationDate && valuationDate) {
			return true;
		}

		if (shareWithInvestors !== shouldShareWithInvestors) {
			return true;
		}

		return securities.some((security) => {
			const currentValue = securityValues[security.id] || '';
			const originalValue = security.fairMarketValuePerUnit?.toString() || '';
			return currentValue !== originalValue;
		});
	};

	const securitiesWithValuesCount = Object.values(securityValues).filter(
		(value) => value && value.trim() !== ''
	).length;

	return (
		<StyledDrawer
			opened={opened}
			onClose={onClose}
			position="right"
			size="md"
			transitionProps={drawerTransitionProps}
			title={tMipManage('root.securities.button.set_valuation')}
		>
			<Flex direction="column" h="100%" mih={400}>
				<LoadingOverlay visible={isLoading} overlayProps={{ blur: 2 }} />

				<div style={{ flex: 1 }}>
					<FormField>
						<Tooltip
							label={
								!aSecurityHasAValuePerUnit
									? tMipManage(
											'root.securities.settings.datePickerDisabledTooltip'
										)
									: ''
							}
							disabled={aSecurityHasAValuePerUnit}
							position="top-start"
							withArrow
						>
							<Box
								style={{
									display: 'inline-block',
									width: '100%',
									cursor: !aSecurityHasAValuePerUnit ? 'not-allowed' : 'default',
								}}
							>
								<Text size="sm" fw={600} mb="xs">
									{tMipManage('root.securities.settings.valuation.date')}
								</Text>
								<DatePickerInput
									placeholder={tMipManage(
										'root.securities.settings.valuation_date_placeholder'
									)}
									value={valuationDate}
									onChange={setValuationDate}
									clearable
									size="md"
									leftSection={<Icon variant="calendar" size="xl" />}
								/>
							</Box>
						</Tooltip>
					</FormField>

					{showShareWithInvestorsSwitch && (
						<FormField>
							<AdvancedSwitch
								selectedValue={shareWithInvestors}
								rightText={tMipManage(
									'root.securities.settings.share_with_investors'
								)}
								onChange={(event) => setShareWithInvestors(!!event)}
								disabled={isLoading}
								selectedColor="dark.8"
								unselectedFontWeight={600}
								unselectedLetterSpacing="0"
								unselectedColor="dark.8"
							/>
						</FormField>
					)}

					{valuationDate && (
						<FormField>
							<Text size="sm" fw={600} mb="xs">
								Value per unit ({securitiesWithValuesCount})
							</Text>
							<Box
								sx={(theme) => ({
									border: `1px solid ${theme.colors.gray[4]}`,
									borderRadius: theme.radius.sm,
									overflow: 'hidden',
								})}
							>
								{securities.map((security) => (
									<Flex
										key={security.id}
										align="center"
										justify="space-between"
										p="xs"
									>
										<Text size="sm" fw={500}>
											{security.name}
										</Text>

										<TextInput
											placeholder="Enter value"
											size="sm"
											value={securityValues[security.id] || ''}
											onChange={(e) =>
												handleSecurityValueChange(
													security.id,
													e.target.value
												)
											}
											disabled={isLoading}
											type="number"
											step="0.01"
											min="0"
										/>
									</Flex>
								))}
							</Box>
						</FormField>
					)}
				</div>

				<Flex direction="column" gap="0.5rem" mt="auto">
					<Button
						onClick={handleUpdate}
						fullWidth
						loading={isLoading}
						disabled={!hasChanges() || isLoading}
					>
						{tMipManage('root.securities.button.update_valuation')}
					</Button>
				</Flex>
			</Flex>
		</StyledDrawer>
	);
};
